'use client';

import { cn } from '@/lib/utils';
import { Link } from 'react-router-dom';
import React, { useState, createContext, useContext, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { MobileHeaderIcons } from '@/components/layout/MobileHeaderIcons';

interface Links {
  label: React.ReactNode;
  href: string;
  icon: React.JSX.Element | React.ReactNode;
  onClick?: () => void;
}

interface SidebarContextProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  animate: boolean;
}

const SidebarContext = createContext<SidebarContextProps | undefined>(
  undefined
);

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};

export const SidebarProvider = ({
  children,
  open: openProp,
  setOpen: setOpenProp,
  animate = true,
}: {
  children: React.ReactNode;
  open?: boolean;
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  animate?: boolean;
}) => {
  const [openState, setOpenState] = useState(false);

  const open = openProp !== undefined ? openProp : openState;
  const setOpen = setOpenProp !== undefined ? setOpenProp : setOpenState;

  return (
    <SidebarContext.Provider value={{ open, setOpen, animate }}>
      {children}
    </SidebarContext.Provider>
  );
};

export const Sidebar = ({
  children,
  open,
  setOpen,
  animate,
}: {
  children: React.ReactNode;
  open?: boolean;
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  animate?: boolean;
}) => {
  return (
    <SidebarProvider open={open} setOpen={setOpen} animate={animate}>
      {children}
    </SidebarProvider>
  );
};

export const SidebarBody = (props: React.ComponentProps<typeof motion.div>) => {
  const { open } = useSidebar();
  return (
    <>
      <DesktopSidebar {...props} />
      <MobileSidebar {...(props as unknown as React.ComponentProps<'div'>)}>
        <motion.div
          className={cn(
            'flex flex-col justify-between h-full pt-8 mt-4'
          )}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.3 }}
        >
          {props.children as React.ReactNode}
        </motion.div>
      </MobileSidebar>
    </>
  );
};

export const DesktopSidebar = ({
  className,
  children,
  ...props
}: React.ComponentProps<typeof motion.div>) => {
  const { open, setOpen, animate } = useSidebar();
  const [isHoverEnabled, setIsHoverEnabled] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  
  // Disable hover during navigation to prevent snap
  useEffect(() => {
    const handleNavigation = () => {
      setIsHoverEnabled(false);
      setTimeout(() => setIsHoverEnabled(true), 600);
    };
    
    window.addEventListener('popstate', handleNavigation);
    return () => window.removeEventListener('popstate', handleNavigation);
  }, []);
  
  return (
    <motion.div
      className={cn(
        'h-screen sticky top-0 px-4 py-6 hidden md:flex md:flex-col flex-shrink-0 relative z-20',
        'bg-white/80 dark:bg-neutral-950/80 backdrop-blur-2xl border-r border-neutral-200 dark:border-white/5',
        'shadow-lg',
        className
      )}
      initial={{ width: animate ? '72px' : '280px' }}
      animate={{
        width: animate ? (open ? '280px' : '72px') : '280px',
      }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 30,
        duration: 0.4,
        width: {
          type: "spring",
          stiffness: 300,
          damping: 30,
        }
      }}
      onMouseEnter={() => {
        if (!open && isHoverEnabled) {
          setOpen(true);
        }
      }}
      onMouseLeave={() => {}}
      {...props}
    >
      {children}
      {/* Toggle button */}
      <button
        onClick={() => {
          setIsHovered(false); // Clear hover state so mouse leave doesn't fight
          setOpen(!open);
        }}
        className="absolute top-1/2 -translate-y-1/2 right-[-12px] z-30 w-6 h-6 rounded-full bg-background border border-border shadow-sm flex items-center justify-center hover:bg-muted transition-colors"
        title={open ? 'Collapse sidebar' : 'Expand sidebar'}
      >
        <motion.svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-muted-foreground"
          animate={{ rotate: open ? 0 : 180 }}
          transition={{ duration: 0.2 }}
        >
          <path d="m15 18-6-6 6-6" />
        </motion.svg>
      </button>
    </motion.div>
  );
};

export const MobileSidebar = ({
  className,
  children,
  ...props
}: React.ComponentProps<'div'>) => {
  const { open: desktopOpen, setOpen: setDesktopOpen } = useSidebar();
  // Separate open state for mobile drawer so it doesn't conflict with desktop hover
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = window.location.pathname;
  const isFilmmaker = location.includes('/filmmaker');

  const homeLink = location.startsWith('/client')
    ? '/client/dashboard'
    : location.includes('/dashboard')
    ? '/filmmaker/dashboard'
    : '/';

  return (
    <div
      className={cn(
        'h-16 px-4 flex flex-row md:hidden items-center justify-between bg-white/80 dark:bg-neutral-950/80 backdrop-blur-xl border-b border-neutral-200 dark:border-white/5 w-full sticky top-0 z-50'
      )}
      {...props}
    >
      <div className="flex items-center justify-between w-full">
        <div className="flex-shrink-0">
          <Link
            to={homeLink}
            className="font-normal flex justify-center items-center text-sm py-1 relative z-20"
          >
            <img
              src="/lovable-uploads/91064b4e-0f45-41a0-8966-0416d131780a.png"
              alt="FindMyFilmmaker Logo"
              className="h-8 w-8 flex-shrink-0 rounded-md object-contain"
            />
            <span className="ml-2 font-medium text-black dark:text-white hidden sm:inline-block">
              FindMyFilmmaker{' '}
              <span className="text-primary">
                {isFilmmaker ? 'Filmmaker' : 'Client'}
              </span>
            </span>
          </Link>
        </div>
        <div className="flex items-center gap-1">
          {/* Mobile header icons for chat and notifications */}
          <MobileHeaderIcons />
          <button
            className="p-2 text-neutral-600 dark:text-neutral-400 hover:text-primary transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>
      {/* Portal the drawer to body to escape stacking context */}
      {createPortal(
        <AnimatePresence mode="wait">
          {mobileOpen && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998]"
                onClick={() => setMobileOpen(false)}
              />

              {/* Sidebar */}
              <motion.div
                initial={{ x: '-100%' }}
                animate={{ x: 0 }}
                exit={{ x: '-100%' }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 30,
                }}
                className={cn(
                  'fixed h-full w-[85%] max-w-sm left-0 top-0 bg-white dark:bg-neutral-900 p-8 z-[9999] flex flex-col justify-between border-r border-neutral-200 dark:border-white/5',
                  'shadow-2xl',
                  className
                )}
              >
                {/* Close button with animation */}
                <motion.div
                  className="absolute right-6 top-6 z-50 text-neutral-800 dark:text-neutral-200 cursor-pointer p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-full transition-colors"
                  onClick={() => setMobileOpen(false)}
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <X className="w-5 h-5" />
                </motion.div>
                <SidebarProvider open={true} setOpen={() => {}} animate={false}>
                  {children}
                </SidebarProvider>
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export const SidebarLink = ({
  link,
  className,
  index = 0,
  ...props
}: {
  link: Links;
  className?: string;
  index?: number;
  props?: React.ComponentProps<typeof Link>;
}) => {
  const { open, animate } = useSidebar();
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.div
      initial={false}
      animate={{
        x: 0,
      }}
      transition={{
        delay: open ? index * 0.03 : 0,
        type: "spring",
        stiffness: 500,
        damping: 30,
      }}
    >
      <Link
        to={link.href}
        className={cn(
          'flex items-center gap-3 group/sidebar py-2.5 px-3 relative overflow-hidden rounded-xl mx-1',
          'transition-all duration-300 ease-in-out',
          !open ? 'justify-center px-0 mx-0 w-10 h-10 ml-1' : 'justify-start w-full',
          className
        )}
        onClick={link.onClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        {...props}
      >
        {/* Icon with sophisticated animation */}
        <motion.div
          animate={{
            scale: isHovered ? 1.1 : 1,
            color: isHovered ? "var(--primary)" : "currentColor",
          }}
          transition={{
            type: "spring",
            stiffness: 400,
            damping: 20,
          }}
          className="relative z-10 flex-shrink-0"
        >
          {link.icon}
        </motion.div>
        
        <motion.span
          initial={false}
          animate={{
            opacity: open ? 1 : 0,
            width: open ? 'auto' : 0,
            x: isHovered && open ? 4 : 0,
          }}
          transition={{
            duration: 0.2,
            opacity: { duration: 0.1 },
            x: { type: "spring", stiffness: 300, damping: 20 }
          }}
          className="text-sm font-medium whitespace-pre overflow-hidden flex-shrink-0 relative z-10 tracking-tight"
        >
          {link.label}
        </motion.span>
      </Link>
    </motion.div>
  );
};
